import React, { useState } from 'react';
import { ICommunity } from '../../../../../../networked-shared-interfaces/interfaces-phase2/platform-admin/platform-admin';
import { IDynamicLinkFormValues, useCommunityDynamicLink } from '../../hooks/useCommunityDynamicLink';

interface IBranchDynamicLinkCardProps {
	communityId: string;
	branchDetails: ICommunity['branchDetails'];
	onRefetch: () => void;
}

const BranchDynamicLinkCard: React.FC<IBranchDynamicLinkCardProps> = ({ communityId, branchDetails, onRefetch }) => {
	const { loading, error, saved, saveDynamicLink } = useCommunityDynamicLink();
	const [showSecret, setShowSecret] = useState(false);

	const hasExisting = !!(branchDetails?.appId || branchDetails?.branch_key);

	const [form, setForm] = useState<IDynamicLinkFormValues>({
		ios: branchDetails?.ios ?? '',
		android: branchDetails?.android ?? '',
		appId: branchDetails?.appId ?? '',
		branch_key: branchDetails?.branch_key ?? '',
		branch_secret: '',
	});

	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

	const handleChange = (field: keyof IDynamicLinkFormValues, value: string) => {
		setForm(f => ({ ...f, [field]: value }));
		if (validationErrors[field]) {
			setValidationErrors(v => {
				const next = { ...v };
				delete next[field];
				return next;
			});
		}
	};

	const validate = (): boolean => {
		const errs: Record<string, string> = {};
		if (!form.appId.trim()) errs.appId = 'App ID is required.';
		if (!form.branch_key.trim()) errs.branch_key = 'Branch key is required.';
		if (!form.branch_secret.trim()) errs.branch_secret = 'Branch secret is required.';
		setValidationErrors(errs);
		return Object.keys(errs).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;
		const ok = await saveDynamicLink(communityId, form);
		if (ok) onRefetch();
	};

	// Read-only view when credentials already exist
	if (hasExisting) {
		return (
			<div className="np-os-card card border-0 shadow-sm mt-3">
				<div className="card-body p-4">
					<div className="d-flex align-items-center mb-3">
						<div className="np-os-icon-wrap mr-3">
							<i className="fas fa-code-branch fa-lg text-success"></i>
						</div>
						<div>
							<h6 className="mb-0 font-weight-bold">Branch Dynamic Link Credentials</h6>
							<small className="text-muted">Credentials are configured and locked.</small>
						</div>
					</div>
					<div className="alert alert-success py-2 mb-0">
						<i className="fas fa-check-circle mr-2"></i>
						<strong>Branch credentials are already configured for this community.</strong>
						<div className="mt-2">
							{branchDetails?.appId && (
								<span className="d-block small">
									<strong>App ID:</strong> <code>{branchDetails.appId}</code>
								</span>
							)}
							{branchDetails?.ios && (
								<span className="d-block small">
									<strong>iOS:</strong> <code>{branchDetails.ios}</code>
								</span>
							)}
							{branchDetails?.android && (
								<span className="d-block small">
									<strong>Android:</strong> <code>{branchDetails.android}</code>
								</span>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Editable form when credentials do not exist yet
	return (
		<div className="np-os-card card border-0 shadow-sm mt-3">
			<div className="card-body p-4">
				<div className="d-flex align-items-center mb-3">
					<div className="np-os-icon-wrap mr-3">
						<i className="fas fa-code-branch fa-lg text-primary"></i>
					</div>
					<div>
						<h6 className="mb-0 font-weight-bold">Branch Dynamic Link Credentials</h6>
						<small className="text-muted">
							Enter your Branch app credentials to link this community with deep-linking support.
						</small>
					</div>
				</div>

				{saved && (
					<div className="alert alert-success py-2 mb-3">
						<i className="fas fa-check-circle mr-2"></i>
						<strong>Branch credentials saved successfully.</strong>
					</div>
				)}

				{error && (
					<div className="alert alert-danger py-2 mb-3">
						<i className="fas fa-exclamation-circle mr-1"></i>
						{error}
					</div>
				)}

				<div className="form-row">
					<div className="form-group col-md-6">
						<label htmlFor="branchAppId">App ID <span className="text-danger">*</span></label>
						<input
							id="branchAppId"
							type="text"
							className={`form-control ${validationErrors.appId ? 'is-invalid' : ''}`}
							placeholder="e.g. 1234567890"
							value={form.appId}
							onChange={e => handleChange('appId', e.target.value)}
							disabled={loading}
						/>
						{validationErrors.appId && <div className="invalid-feedback">{validationErrors.appId}</div>}
					</div>
					<div className="form-group col-md-6">
						<label htmlFor="branchKey">Branch Key <span className="text-danger">*</span></label>
						<input
							id="branchKey"
							type="text"
							className={`form-control ${validationErrors.branch_key ? 'is-invalid' : ''}`}
							placeholder="key_live_..."
							value={form.branch_key}
							onChange={e => handleChange('branch_key', e.target.value)}
							disabled={loading}
						/>
						{validationErrors.branch_key && <div className="invalid-feedback">{validationErrors.branch_key}</div>}
					</div>
				</div>

				<div className="form-row">
					<div className="form-group col-md-6">
						<label htmlFor="branchSecret">Branch Secret <span className="text-danger">*</span></label>
						<div className="input-group">
							<input
								id="branchSecret"
								type={showSecret ? 'text' : 'password'}
								className={`form-control ${validationErrors.branch_secret ? 'is-invalid' : ''}`}
								placeholder="secret_live_..."
								value={form.branch_secret}
								onChange={e => handleChange('branch_secret', e.target.value)}
								disabled={loading}
							/>
							<div className="input-group-append">
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setShowSecret(s => !s)}
									tabIndex={-1}
								>
									<i className={`fas ${showSecret ? 'fa-eye-slash' : 'fa-eye'}`}></i>
								</button>
							</div>
							{validationErrors.branch_secret && <div className="invalid-feedback">{validationErrors.branch_secret}</div>}
						</div>
					</div>
				</div>

				<div className="form-row">
					<div className="form-group col-md-6">
						<label htmlFor="branchIos">iOS URL <span className="text-muted small">(optional)</span></label>
						<input
							id="branchIos"
							type="text"
							className="form-control"
							placeholder="https://..."
							value={form.ios}
							onChange={e => handleChange('ios', e.target.value)}
							disabled={loading}
						/>
					</div>
					<div className="form-group col-md-6">
						<label htmlFor="branchAndroid">Android URL <span className="text-muted small">(optional)</span></label>
						<input
							id="branchAndroid"
							type="text"
							className="form-control"
							placeholder="https://..."
							value={form.android}
							onChange={e => handleChange('android', e.target.value)}
							disabled={loading}
						/>
					</div>
				</div>

				<button
					type="button"
					className="np-os-btn btn btn-primary btn-lg px-5"
					onClick={handleSave}
					disabled={loading}
				>
					{loading ? (
						<>
							<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
							Saving…
						</>
					) : (
						<>
							<i className="fas fa-save mr-2"></i>
							Save Branch Credentials
						</>
					)}
				</button>
			</div>
		</div>
	);
};

export default BranchDynamicLinkCard;
